import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';

import { useContents } from 'features/contents';
import { createUrl, request } from 'app/utils';
import { Flex, Loading } from 'components';
import { QuizSidebar, QuizQuestion } from './components';
import { ContentQuizQuestionApiResponse, ContentQuizQuestionsState, ContentQuizQuestionState } from 'features/contents/types';
import { useSchool } from 'features/school';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from 'features/alert';

const ContentQuizView = (): ReactElement => {
  const [loadingEdit, setLoadingEdit] = useState(false);
  const { contentDetails } = useContents();
  const { apiGatewayUrl } = useSchool();
  const navigate = useNavigate();
  const { setAlert } = useAlert();

  const [questionsCompleted, setQuestionsCompleted] = useState(false);
  const [questionsSended, setQuestionsSended] = useState(false);

  const { productSlug, productClassUuid, moduleUuid } = useParams();

  if (contentDetails && contentDetails.quiz && !Boolean(contentDetails.quiz.questions.length)) {
    navigate(createUrl('productModule', [productSlug, productClassUuid, moduleUuid]));
    setAlert({ type: 'error', message: 'Ocorreu um erro ao obter informações do quiz.' });
  }

  const [questions, updateQuestionsProcess] = useState<ContentQuizQuestionsState>(
    contentDetails && contentDetails.quiz
      ? contentDetails.quiz.questions.reduce((prev, question, index) => {
          return {
            ...prev,
            [question.questionUuid]: {
              ...question,
              currentQuestion: index === 0,
              questionIndex: index + 1,
              isFirst: index === 0,
              isLast:
                index + 1 === (contentDetails && contentDetails.quiz && contentDetails.quiz.questions ? contentDetails.quiz.questions.length : -1)
                  ? true
                  : false,

              prevQuestionUuid:
                index === 0
                  ? ''
                  : contentDetails && contentDetails.quiz && contentDetails.quiz.questions
                  ? contentDetails.quiz.questions[index - 1].questionUuid
                  : '',
              nextQuestionUuid:
                index + 1 === (contentDetails && contentDetails.quiz && contentDetails.quiz.questions ? contentDetails.quiz.questions.length : -1)
                  ? ''
                  : contentDetails && contentDetails.quiz && contentDetails.quiz.questions
                  ? contentDetails.quiz.questions[index + 1].questionUuid
                  : '',
            },
          };
        }, {})
      : undefined!
  );

  const firstQuestionUuid =
    contentDetails.quiz?.questions && contentDetails.quiz?.questions.length ? contentDetails.quiz.questions[0].questionUuid : '';

  const [currentQuestion, setCurrentQuestion] = useState<string>(firstQuestionUuid);

  const updateQuestions = useCallback(
    (question: ContentQuizQuestionState) => {
      if (question.currentQuestion) {
        if (currentQuestion && currentQuestion !== question.questionUuid) {
          updateQuestionsProcess((prevQuestions) => ({
            ...prevQuestions,
            [currentQuestion]: { ...prevQuestions[currentQuestion], currentQuestion: false },
          }));
        }
        setCurrentQuestion(question.questionUuid);
      }

      updateQuestionsProcess((prevQuestions) => ({
        ...prevQuestions,
        [question.questionUuid]: question,
      }));
    },
    [questions]
  );

  const sendQuestions = useCallback(() => {
    setLoadingEdit(true);

    request({
      url: `/api/v1/School/AssessContentQuizAnswers`,
      method: 'post',
      baseURL: apiGatewayUrl,
      data: {
        contentUuid: contentDetails.contentUuid,
        productClassUuid: contentDetails.productClassUuid,
        quizAnswers: Object.keys(questions).reduce((prev, questionUuid) => {
          const question = questions[questionUuid];

          return { ...prev, [question.questionUuid]: question.answerUuid };
        }, {}),
      },
    })
      .then((response: AxiosResponse<ContentQuizQuestionApiResponse>) => {
        const { results } = response.data;
        setLoadingEdit(false);

        setCurrentQuestion(firstQuestionUuid);

        setTimeout(() => {
          results.forEach((question) => {
            updateQuestionsProcess((prevQuestions) => ({
              ...prevQuestions,
              [question.questionUuid]: {
                ...prevQuestions[question.questionUuid],
                ...question,
              },
            }));
          });
        }, 10);

        setQuestionsSended(true);
      })
      .catch(() => {
        setLoadingEdit(false);
      });
  }, [questions]);

  useEffect(() => {
    window.scroll({ top: 0, behavior: 'smooth' });

    updateQuestionsProcess((prevQuestions) => {
      const newQuestions = Object.keys(prevQuestions).reduce((prev, questionUuid) => {
        const question = prevQuestions[questionUuid];
        return {
          ...prev,
          [question.questionUuid]: {
            ...question,
            currentQuestion: currentQuestion === question.questionUuid,
          },
        };
      }, {});

      return newQuestions;
    });
  }, [currentQuestion]);

  useEffect(() => {
    if (!questions) return;

    setQuestionsCompleted(
      Boolean(
        Object.keys(questions).reduce((prev, questionUuid, index) => {
          if (index && !prev) {
            return false;
          }

          return Boolean(questions[questionUuid].answerUuid);
        }, false)
      )
    );
  }, [questions]);

  if (!(contentDetails && contentDetails.quiz) || !questions) return <></>;

  return (
    <>
      {loadingEdit && <Loading alpha />}

      <Flex flexWrap="nowrap">
        <Flex bg="quizQuestionsBg">
          <QuizQuestion
            currentQuestion={currentQuestion}
            setCurrentQuestion={setCurrentQuestion}
            questions={questions}
            updateQuestions={updateQuestions}
            questionsCompleted={questionsCompleted}
            sendQuestions={sendQuestions}
            questionsSended={questionsSended}
          />
        </Flex>

        <Flex display={{ m: 'none', d: 'flex' }} flexGrow={0} bg="quizSidebarBg" flexShrink={0} width={{ m: '100vw', d: 425 }}>
          <QuizSidebar
            questions={questions}
            setCurrentQuestion={setCurrentQuestion}
            questionsCompleted={questionsCompleted}
            sendQuestions={sendQuestions}
            questionsSended={questionsSended}
          />
        </Flex>
      </Flex>
    </>
  );
};
export default ContentQuizView;
